import { PageLoading } from 'module/ui/components/atoms/loading';
import { TextFormattingToolbar } from 'module/ui/components/atoms/text-formatting-toolbar';
import { PageType } from 'api/page-template/types';
import { Document as DocumentType } from 'api/pages';
import { useGetDocumentFeedbackQuery } from 'queries/feedback';
import {
  FC,
  RefObject,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useDefaultAskAiConfig } from 'components/organisms/document-editor/extensions/ask-ai/default-config';
import { useDefaultCommands } from 'components/organisms/document-editor/extensions/command-picker/commands-list/default-commands';
import { useDefaultCustomGroupSectionNodeResolver } from 'components/organisms/document-editor/extensions/custom-group-section/default-resolver';
import { useDefaultCustomSectionNodeResolver } from 'components/organisms/document-editor/extensions/custom-section/default-resolver';
import { ExtensionConfig } from 'components/organisms/document-editor/types';
import { CollaborativeEditor } from '../document-editor';
import { useAutoSaveDocument } from './use-save-document';
import { CollaborationUser } from '../document-editor/collaborative-editor';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: var(--spacing-05);
  align-items: center;
  overflow-y: auto;
`;
const EditorWrapper = styled.div`
  font-size: var(--font-size-lg);
  display: flex;
  flex-direction: column;
  padding: var(--spacing-05);
  width: 100%;

  .col-group {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-05);

    .table-of-contents {
      position: absolute;
      align-items: center;
      justify-content: center;
      display: flex;
      top: 20%;
      padding: 0 40px 40px 0;
    }
  }

  .sidebar {
    width: 30px;
  }

  .main {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: auto;
    width: 100%;
    justify-content: center;
    align-items: center;
    position: relative;

    .rte-editor {
      width: 768px;
    }
  }

  p {
    font-size: var(--font-size-lg);
    margin: var(--spacing-05) 0 var(--spacing-05) 0;
    line-height: var(--line-height-05);
  }

  ol,
  ul {
    padding: 0px 0px 0px 26px;
    margin: 0px;
  }

  table td,
  table th {
    padding: 10px 24px 10px 0px;
  }

  h1,
  h2,
  h3 {
    font-weight: var(--font-weight-regular);
    line-height: var(--line-height-01);
  }

  h1 {
    font-size: var(--font-size-xxl);
    margin: 0 0 8px;
  }

  h2 {
    font-size: var(--font-size-xxl);
    margin: 32px 0px 8px;
  }

  h3 {
    font-size: 1.14rem;
    margin: 40px 0px 8px;
  }
`;

interface Props {
  pageType?: PageType;
  document: DocumentType;
  readonly?: boolean;
  sourceId?: number;
  isChatOpen?: boolean;
  isDocumentReadOnly?: boolean;
  placeholder?: string;
  onDistractionFreeStateChange?: (state: boolean) => void;
  editorExtensionsConfig?: Partial<ExtensionConfig>;
  onActiveUsersFromCollaborationChange?: (users: CollaborationUser[]) => void;
  hideTitle?: boolean;
  scrollContainerRef?: RefObject<DocumentScrollHandle>;
}

export interface DocumentScrollHandle {
  scrollToSection(sectionId: number): void;
}
export const Document: FC<Props> = ({
  document,
  pageType,
  sourceId,
  isChatOpen,
  readonly = true,
  isDocumentReadOnly = false,
  placeholder,
  onDistractionFreeStateChange,
  editorExtensionsConfig: extensionConfigFromProps,
  onActiveUsersFromCollaborationChange,
  hideTitle = false,
  scrollContainerRef,
}) => {
  const [isElementInspectorOpen, setIsElementInspectorOpen] = useState(false);
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    scrollContainerRef,
    () => ({
      scrollToSection(sectionId: number) {
        const scrollContainer = contentWrapperRef.current;
        const section = scrollContainer?.querySelector<HTMLElement>(
          `[data-section-id="${sectionId}"]`,
        );

        if (!scrollContainer || !section) {
          return;
        }

        const containerRect = scrollContainer.getBoundingClientRect();
        const sectionRect = section.getBoundingClientRect();
        const offsetTop =
          scrollContainer.scrollTop +
          (sectionRect.top - containerRect.top) -
          24;

        scrollContainer.scrollTo({
          top: Math.max(0, offsetTop),
          behavior: 'smooth',
        });
      },
    }),
    [],
  );

  const commands = useDefaultCommands({ pageType });
  const askAiConfig = useDefaultAskAiConfig({
    documentId: document.metadata.documentId,
  });
  const { data: feedbackData } = useGetDocumentFeedbackQuery(
    'DOCUMENT',
    document.metadata.documentId.toString(),
  );

  const customSectionResolver = useDefaultCustomSectionNodeResolver({
    documentId: document.metadata.documentId,
  });
  const customGroupSectionResolver = useDefaultCustomGroupSectionNodeResolver({
    documentId: document.metadata.documentId,
  });
  const { saveDocument, refetchDocument } = useAutoSaveDocument(
    document.metadata.documentId,
  );

  const editorExtensionsConfig: Partial<ExtensionConfig> = useMemo(() => {
    return {
      askAiConfig: {
        enabled: true,
        ...askAiConfig,
      },
      distractionFreeConfig: {
        onDistractionFreeStateChange:
          onDistractionFreeStateChange ?? (() => {}),
      },
      commandsPickerConfig: {
        enabled: true,
        items: commands.items,
      },
      tableToolbarConfig: {
        enabled: isDocumentReadOnly === true ? false : true,
      },
      customSectionConfig: {
        ...(extensionConfigFromProps?.customSectionConfig ?? {}),
        allowResolve: true as const,
        resolver: customSectionResolver,
        pageType: pageType,
      },
      endOfDocumentConfig: {
        enabled: true,
      },
      likeDislikeConfig: {
        enabled: readonly,
      },
      collaborationConfig: {
        enabled: readonly ? false : true,
        documentName: document.metadata.editorDocumentName,
        onActiveUsersChange: (users) => {
          onActiveUsersFromCollaborationChange?.(users);
        },
      },
      autoSaveConfig: readonly
        ? {
            enabled: false as const,
          }
        : {
            enabled: true as const,
            save: saveDocument,
            onSaveSuccess: refetchDocument,
          },
      elementInspectorConfig: {
        enabled: isElementInspectorOpen,
        pageType: pageType,
        sourceId: sourceId,
        sources:
          document.metadata?.documentSourceMappingModel?.metadata?.sources,
        isDefaultReadOnly: readonly,
      },
      tableOfContentsConfig: {
        enabled: true,
      },
      ...extensionConfigFromProps,
      groupSectionConfig: {
        ...extensionConfigFromProps?.groupSectionConfig,
        allowResolve: true,
        resolver: customGroupSectionResolver,
      },
    };
  }, [
    askAiConfig,
    onDistractionFreeStateChange,
    commands.items,
    customSectionResolver,
    customGroupSectionResolver,
    readonly,
    document.metadata.editorDocumentName,
    document.metadata?.documentSourceMappingModel?.metadata?.sources,
    extensionConfigFromProps,
    onActiveUsersFromCollaborationChange,
    saveDocument,
    refetchDocument,
    isElementInspectorOpen,
    pageType,
    isDocumentReadOnly,
    sourceId,
  ]);

  const feedbackPayload = useMemo(() => {
    return {
      sourceId: document.metadata.documentId.toString(),
      sourceType: 'DOCUMENT',
      feedbackData: feedbackData ?? [],
      parentId: document.metadata.documentId.toString(),
      parentType: 'DOCUMENT',
    };
  }, [document.metadata.documentId, feedbackData]);
  const [isEditorInitialized, setIsEditorInitialized] = useState(false);
  const onInitialize = useCallback(() => {
    setIsEditorInitialized(true);
  }, []);

  return commands.isLoading ? (
    <PageLoading />
  ) : (
    <ContentWrapper
      ref={contentWrapperRef}
      className="document-content-wrapper"
    >
      <EditorWrapper className="document-editor-wrapper">
        <CollaborativeEditor
          feedbackPayload={feedbackPayload}
          value={document.content}
          extensionConfig={editorExtensionsConfig}
          readonly={readonly}
          placeholder={placeholder}
          className={isElementInspectorOpen ? 'ask-ai-inspect-mode' : ''}
          onInitialize={onInitialize}
          isChatOpen={isChatOpen}
          documentTitle={hideTitle ? undefined : document.metadata.title}
          pageType={pageType}
        />
        {isEditorInitialized &&
          !isChatOpen &&
          readonly &&
          (pageType === PageType.Meeting ||
            pageType === PageType.Email ||
            PageType.Company) &&
          document.metadata?.documentSourceMappingModel?.pageType !==
            PageType.Manual && (
            <TextFormattingToolbar
              inspectorEnabled={isElementInspectorOpen}
              setIsElementInspectorOpen={setIsElementInspectorOpen}
              pageType={pageType}
              title={document.metadata.title}
              documentId={sourceId}
              documentSources={
                document.metadata?.documentSourceMappingModel?.metadata?.sources
              }
            />
          )}
      </EditorWrapper>
    </ContentWrapper>
  );
};
